import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Scale, Landmark, CreditCard, TrendingUp, TrendingDown, Search, Calendar, Tag, AlertTriangle, Check } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (v) => v != null ? Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const fmtDate = (d) => { if (!d) return '—'; const p = d.split('-'); if (p.length !== 3) return d; return `${p[2]}-${p[1]}-${p[0].slice(2)}`; };
const fyDates = (fyLabel) => {
  const m = fyLabel.match(/(\d{4})-(\d{2})/);
  if (!m) return { start: '—', end: '—' };
  return { start: `01-04-${m[1].slice(2)}`, end: `31-03-${m[2]}` };
};

const TAG_STYLES = {
  business: 'bg-blue-100 text-blue-700',
  personal: 'bg-purple-100 text-purple-700',
  income: 'bg-green-100 text-green-700',
  junk: 'bg-gray-100 text-gray-500',
  minor: 'bg-yellow-50 text-yellow-700',
};
const TagBadge = ({ tag, category }) => {
  if (!tag || tag === 'untagged') return <span className="text-[10px] text-orange-500 font-medium whitespace-nowrap">Untagged</span>;
  // CC Payment entries show as "Payment Made" with source bank
  const isCCPayment = category && category.toLowerCase().includes('cc payment');
  if (isCCPayment) {
    const bankMatch = category.match(/from\s+(.+?)\)?$/i);
    const bankName = bankMatch ? bankMatch[1].replace(/\)$/, '') : '';
    return (
      <span title={category}
        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap bg-blue-50 text-blue-600">
        Payment Made{bankName ? ` (${bankName})` : ''}
      </span>
    );
  }
  const parts = tag.split('/').map(p => p.trim());
  const primaryLower = parts[0].toLowerCase();
  // Business/Personal: show primary only (category has the detail)
  // Income: skip "Income" prefix, show the sub-part (e.g. "Salary", "Business")
  let displayLabel;
  if (primaryLower === 'income' && parts.length > 1) {
    displayLabel = parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' / ');
  } else {
    displayLabel = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
  }
  return (
    <span title={tag}
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${TAG_STYLES[primaryLower] || 'bg-gray-100 text-gray-600'}`}>
      {displayLabel}
    </span>
  );
};

// Extract clean last-4 from account field like "2697 (Punit)" → "2697"
const acctLast4 = (a) => (a || '').replace(/\s*\(.*\)/, '').trim();

// Build short tab label: "DBS-2697" or "SCB-2800 (Punit)" for bank, "AMEX" for CC
const acctLabel = (source, account, holder, type) => {
  const abbr = { 'Standard Chartered': 'SCB', 'IDFC FIRST Bank': 'IDFC', 'AMERICAN EXPRESS': 'AMEX' };
  const short = abbr[source] || source;
  if (type === 'credit_card') return short;  // CC: just source name, no card number
  const last4 = acctLast4(account);
  const suffix = holder ? ` (${holderShort(holder)})` : '';
  return `${short}-${last4}${suffix}`;
};

const holderShort = (h) => {
  if (!h) return '';
  if (h.toUpperCase().includes('HUF')) return 'HUF';
  if (h.toUpperCase().includes('GOKUL')) return 'Punit';
  return h.split(' ')[0].charAt(0).toUpperCase() + h.split(' ')[0].slice(1).toLowerCase();
};

export default function ReconciliationPage() {
  const [bankData, setBankData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topTab, setTopTab] = useState('bank');
  const [selectedAcctKey, setSelectedAcctKey] = useState(null);
  const [selectedFY, setSelectedFY] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHolder, setSelectedHolder] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reconciliation/bank-monthly`, { withCredentials: true });
      setBankData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isBankTab = topTab === 'bank';
  const tabData = bankData.filter(a => isBankTab ? a.type !== 'credit_card' : a.type === 'credit_card');

  // Deduplicate accounts by source+account+holder key, merge FYs
  const uniqueAccounts = useMemo(() => {
    const map = new Map();
    tabData.forEach(a => {
      const key = `${a.source}|${acctLast4(a.account)}|${a.holder || ''}`;
      if (!map.has(key)) {
        map.set(key, { ...a, _key: key, financial_years: [...(a.financial_years || [])] });
      } else {
        // Merge FYs — avoid duplicate FY labels
        const existing = map.get(key);
        const existingFYs = new Set(existing.financial_years.map(f => f.fy));
        (a.financial_years || []).forEach(fy => {
          if (!existingFYs.has(fy.fy)) {
            existing.financial_years.push(fy);
          } else {
            // Same FY exists — merge entries (pick the one with more entries)
            const idx = existing.financial_years.findIndex(f => f.fy === fy.fy);
            if (idx >= 0 && fy.entries.length > existing.financial_years[idx].entries.length) {
              existing.financial_years[idx] = fy;
            }
          }
        });
      }
    });
    // Sort FYs within each account
    map.forEach(a => a.financial_years.sort((x, y) => x.fy.localeCompare(y.fy)));
    return [...map.values()];
  }, [tabData]);

  // Build tab list from unique accounts — sort Punit first, non-primary DBS holders at the end
  const accountTabs = useMemo(() => {
    const sorted = [...uniqueAccounts].sort((a, b) => {
      const ha = (a.holder || '').toUpperCase();
      const hb = (b.holder || '').toUpperCase();
      // Non-primary DBS holders (HUF, Laksh, Devanssh) go to the very end
      const isSecondaryA = a.source === 'DBS' && ha && !ha.includes('GOKUL');
      const isSecondaryB = b.source === 'DBS' && hb && !hb.includes('GOKUL');
      if (isSecondaryA !== isSecondaryB) return isSecondaryA ? 1 : -1;
      if (a.source !== b.source) return a.source.localeCompare(b.source);
      const pa = ha.includes('GOKUL') ? 0 : ha.includes('HUF') ? 1 : ha ? 2 : 3;
      const pb = hb.includes('GOKUL') ? 0 : hb.includes('HUF') ? 1 : hb ? 2 : 3;
      return pa - pb;
    });
    return sorted.map(a => {
      const allReconciled = (a.financial_years || []).length > 0 &&
        (a.financial_years || []).every(fy => !fy.has_mismatches);
      return {
        key: a._key,
        label: acctLabel(a.source, a.account, a.holder, a.type),
        source: a.source,
        reconciled: allReconciled,
        closed: !!a.is_closed,
      };
    });
  }, [uniqueAccounts]);

  const effectiveKey = selectedAcctKey && accountTabs.some(t => t.key === selectedAcctKey)
    ? selectedAcctKey : (accountTabs[0]?.key || null);

  const selected = uniqueAccounts.find(a => a._key === effectiveKey) || null;

  // Collect all FY labels for the selected account
  const allFYs = useMemo(() => {
    if (!selected) return [];
    return [...new Set(selected.financial_years.map(f => f.fy))].sort();
  }, [selected]);

  // Default to all FYs; respect explicit user selection
  const effectiveFY = useMemo(() => {
    if (selectedFY === 'all') return 'all';
    if (selectedFY !== '__auto__' && allFYs.includes(selectedFY)) return selectedFY;
    return 'all';
  }, [selectedFY, allFYs]);

  const shouldShowFY = useCallback((fyLabel) => effectiveFY === 'all' || fyLabel === effectiveFY, [effectiveFY]);

  const getFilteredEntries = useCallback((entries) => {
    let filtered = entries;
    // Filter by cardholder + card sub-tab
    if (selectedHolder !== 'all') {
      const [holderName, cardNum] = selectedHolder.split('|');
      filtered = filtered.filter(e => e.cardholder === holderName && e.card_number === cardNum);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        (e.merchant || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q) ||
        (e.tag || '').toLowerCase().includes(q) ||
        (e.date || '').includes(q) ||
        (e.cardholder || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [searchQuery, selectedHolder]);

  // Cardholder sub-tabs for the selected account (only if multiple holder+card combos)
  const holderTabs = useMemo(() => {
    if (!selected || !selected.cardholders || selected.cardholders.length <= 1) return [];
    return selected.cardholders;
  }, [selected]);

  const isHolderFiltered = selectedHolder !== 'all';

  // Totals for selected account and FY
  const totals = useMemo(() => {
    if (!selected) return { ob: 0, cb: 0, debits: 0, credits: 0 };
    const visibleFYs = selected.financial_years.filter(fy => shouldShowFY(fy.fy));
    if (visibleFYs.length === 0) return { ob: 0, cb: 0, debits: 0, credits: 0 };
    if (isHolderFiltered) {
      // Per-cardholder view: compute debits/credits from filtered entries only, no OB/CB
      const allEntries = visibleFYs.flatMap(fy => getFilteredEntries(fy.entries || []));
      return {
        ob: null, cb: null,
        debits: allEntries.reduce((s, e) => s + (e.debit || 0), 0),
        credits: allEntries.reduce((s, e) => s + (e.credit || 0), 0),
      };
    }
    return {
      ob: visibleFYs[0].opening_balance || 0,
      cb: visibleFYs[visibleFYs.length - 1].closing_balance || 0,
      debits: visibleFYs.reduce((s, fy) => s + (fy.total_debits || 0), 0),
      credits: visibleFYs.reduce((s, fy) => s + (fy.total_credits || 0), 0),
    };
  }, [selected, shouldShowFY, isHolderFiltered, getFilteredEntries]);

  // Entry count for display
  const entryCount = useMemo(() => {
    if (!selected) return 0;
    return selected.financial_years.reduce((s, fy) => s + (shouldShowFY(fy.fy) ? fy.total_transactions : 0), 0);
  }, [selected, shouldShowFY]);

  // Untagged count across ALL FYs (not restricted by FY filter)
  const untaggedCount = useMemo(() => {
    if (!selected) return { untagged: 0, total: 0 };
    let untagged = 0, total = 0;
    selected.financial_years.forEach(fy => {
      (fy.entries || []).forEach(e => {
        total++;
        if (!e.tag || e.tag === 'untagged') untagged++;
      });
    });
    return { untagged, total };
  }, [selected]);

  // FYs that have mismatches
  const fyMismatchSet = useMemo(() => {
    if (!selected) return new Set();
    return new Set(
      selected.financial_years
        .filter(fy => fy.has_mismatches)
        .map(fy => fy.fy)
    );
  }, [selected]);

  // Mismatch data for selected account+FY
  const mismatches = useMemo(() => {
    if (!selected) return [];
    const visibleFYs = selected.financial_years.filter(fy => shouldShowFY(fy.fy));
    return visibleFYs.flatMap(fy => (fy.mismatches || []).map(m => ({ ...m, fy: fy.fy })));
  }, [selected, shouldShowFY]);

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-full">
      <div className="text-[#5C5C55] text-sm">Loading reconciliation data...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-5" data-testid="reconciliation-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-[#1C1C19]">Reconciliation</h1>
        <p className="text-sm text-[#5C5C55] mt-1">FY-wise ledger with all entries</p>
      </div>

      {/* Top-level tabs */}
      <div className="flex items-center gap-1 bg-[#E8E6DE] p-1 rounded-lg w-fit">
        <TabBtn active={isBankTab} onClick={() => { setTopTab('bank'); setSelectedAcctKey(null); setSelectedFY('all'); setSearchQuery(''); }}
          label="Bank Accounts" testId="recon-top-bank" icon={<Landmark size={15} />} />
        <TabBtn active={!isBankTab} onClick={() => { setTopTab('cc'); setSelectedAcctKey(null); setSelectedFY('all'); setSearchQuery(''); }}
          label="Credit Cards" testId="recon-top-cc" icon={<CreditCard size={15} />} />
      </div>

      {/* Individual account tabs */}
      <div className="flex items-center gap-1 bg-[#E8E6DE] p-1 rounded-lg overflow-x-auto">
        {accountTabs.filter(t => !t.closed).map(t => (
          <TabBtn key={t.key} active={effectiveKey === t.key}
            onClick={() => { setSelectedAcctKey(t.key); setSelectedFY('all'); setSearchQuery(''); setSelectedHolder('all'); }}
            label={t.label} testId={`recon-tab-${t.key.replace(/[|\s]+/g, '-')}`}
            icon={isBankTab ? <Landmark size={13} /> : <CreditCard size={13} />}
            reconciled={t.reconciled} closed={false} />
        ))}
        {accountTabs.some(t => t.closed) && <>
          <div className="w-px h-5 bg-[#C5C3BA] mx-1 shrink-0" />
          {accountTabs.filter(t => t.closed).map(t => (
            <TabBtn key={t.key} active={effectiveKey === t.key}
              onClick={() => { setSelectedAcctKey(t.key); setSelectedFY('all'); setSearchQuery(''); setSelectedHolder('all'); }}
              label={t.label} testId={`recon-tab-${t.key.replace(/[|\s]+/g, '-')}`}
              icon={isBankTab ? <Landmark size={13} /> : <CreditCard size={13} />}
              reconciled={t.reconciled} closed={true} />
          ))}
        </>}
      </div>

      {/* Cardholder sub-tabs (for CCs with multiple holders like AMEX) */}
      {holderTabs.length > 0 && (
        <div className="flex items-center gap-1 bg-[#F3F2ED] p-1 rounded-lg overflow-x-auto">
          <button onClick={() => setSelectedHolder('all')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${selectedHolder === 'all' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:bg-white/50'}`}>
            All Cardholders
          </button>
          {holderTabs.map(h => {
            const filterKey = `${h.name}|${h.card}`;
            return (
              <button key={filterKey} onClick={() => setSelectedHolder(filterKey)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${selectedHolder === filterKey ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:bg-white/50'}`}>
                {holderShort(h.name)} ({h.card}) <span className="text-[#8A8A80] ml-1">{h.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Filter bar: Search + FY dropdown */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="recon-filters">
        <div className="relative flex-1 min-w-[160px] max-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C55]" />
          <Input data-testid="recon-search" placeholder="Search transactions..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} className="pl-8 border-[#E2E0D8] h-8 text-xs" />
        </div>
        <Select value={effectiveFY} onValueChange={(v) => setSelectedFY(v)}>
          <SelectTrigger data-testid="recon-filter-fy" className="w-[130px] h-8 border-[#E2E0D8] text-xs">
            <Calendar size={12} className="mr-1 text-[#5C5C55]" /><SelectValue placeholder="FY" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All FYs</SelectItem>
            {allFYs.map(fy => (
              <SelectItem key={fy} value={fy}>
                <span className="flex items-center gap-1.5">
                  {fy}
                  {fyMismatchSet.has(fy) && <AlertTriangle size={10} className="text-amber-500" />}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-[#5C5C55] ml-auto font-mono">{entryCount} entries</span>
      </div>

      {/* Summary Cards */}
      <div className={`grid gap-3 ${isHolderFiltered ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-5'}`}>
        {!isHolderFiltered && <SummaryCard icon={isBankTab ? <Landmark size={15} /> : <CreditCard size={15} />} label={!isBankTab ? "Outstanding Balance" : "Opening Balance"} value={`₹${fmtINR(totals.ob)}`} color="text-[#5C5C55]" sub="Earliest FY" />}
        <SummaryCard icon={<TrendingUp size={15} />} label="Total Credits" value={`₹${fmtINR(totals.credits)}`} color="text-[#4A7A5D]" sub="All inflows" />
        <SummaryCard icon={<TrendingDown size={15} />} label="Total Debits" value={`₹${fmtINR(totals.debits)}`} color="text-orange-600" sub="All outflows" />
        {!isHolderFiltered && <SummaryCard icon={<Scale size={15} />} label={!isBankTab ? "Outstanding Dues" : "Closing Balance"} value={`₹${fmtINR(totals.cb)}`} color="text-[#1C1C19]" sub="As of today" />}
        <SummaryCard icon={<Tag size={15} />} label="Untagged" value={`${untaggedCount.untagged}`} color="text-orange-500" sub="Across all dates" />
      </div>

      {/* Statement Mismatch Flags */}
      {mismatches.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3.5 space-y-2" data-testid="mismatch-flags">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-800">
              Statement Mismatch{mismatches.length > 1 ? 'es' : ''} Detected
            </span>
            <span className="text-[10px] text-amber-600 ml-auto">Parsed totals differ from reported statement totals</span>
          </div>
          {mismatches.map((mm, i) => {
            const monthLabel = mm.month ? new Date(mm.month + '-15').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : mm.month;
            return (
              <div key={i} className="bg-white/80 border border-amber-200 rounded px-3 py-2" data-testid={`mismatch-item-${mm.month}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-mono font-semibold text-amber-800">{monthLabel}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px]">
                  {mm.reported_debits !== mm.computed_debits && (
                    <>
                      <span className="text-[#5C5C55]">Debit gap:</span>
                      <span className="font-mono text-orange-700">
                        ₹{fmtINR(Math.abs(mm.reported_debits - mm.computed_debits))}
                        <span className="text-[#8A8A80] ml-1">
                          (reported ₹{fmtINR(mm.reported_debits)} vs parsed ₹{fmtINR(mm.computed_debits)})
                        </span>
                      </span>
                    </>
                  )}
                  {mm.reported_credits !== mm.computed_credits && (
                    <>
                      <span className="text-[#5C5C55]">Credit gap:</span>
                      <span className="font-mono text-orange-700">
                        ₹{fmtINR(Math.abs(mm.reported_credits - mm.computed_credits))}
                        <span className="text-[#8A8A80] ml-1">
                          (reported ₹{fmtINR(mm.reported_credits)} vs parsed ₹{fmtINR(mm.computed_credits)})
                        </span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ledger Tables */}
      {!selected ? (
        <div className="bg-white border border-[#E2E0D8] rounded-lg p-8 text-center text-[#5C5C55]">
          No reconciliation data available
        </div>
      ) : (() => {
        const visibleFYs = selected.financial_years.filter(fy => shouldShowFY(fy.fy));
        if (visibleFYs.length === 0) return (
          <div className="bg-white border border-[#E2E0D8] rounded-lg p-8 text-center text-[#5C5C55]">
            No data for selected FY
          </div>
        );

        // Merge all visible FYs into one continuous ledger
        const firstFY = visibleFYs[0];
        const lastFY = visibleFYs[visibleFYs.length - 1];
        const { start: obDate } = fyDates(firstFY.fy);
        const allEntries = visibleFYs.flatMap(fy => getFilteredEntries(fy.entries || []))
          .filter(e => !(e.merchant || '').toUpperCase().includes('BALANCE FORWARD'));
        // Use today's date for closing row (balance is as-of-today, not FY-end)
        const today = new Date();
        const todayStr = `${String(today.getDate()).padStart(2,'0')}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getFullYear()).slice(2)}`;
        const lastEntryDate = todayStr;
        const rawDebits = visibleFYs.reduce((s, fy) => s + (fy.total_debits || 0), 0);
        const rawCredits = visibleFYs.reduce((s, fy) => s + (fy.total_credits || 0), 0);
        const ob = firstFY.opening_balance || 0;
        // Include opening balance in closing row totals so they balance when CB=0
        const isCreditCard = selected.type === 'credit_card';
        const totalDebits = isCreditCard ? rawDebits + ob : rawDebits;
        const totalCredits = isCreditCard ? rawCredits : rawCredits + ob;

        return (
          <div className="bg-white border border-[#E2E0D8] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" style={{tableLayout:'fixed'}}>
                <thead>
                  <tr className="border-b border-[#E2E0D8] bg-white">
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#8A8A80] uppercase tracking-wider" style={{width:'10%'}}>Date</th>
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-[#8A8A80] uppercase tracking-wider" style={{width:'30%'}}>Description</th>
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-[#8A8A80] uppercase tracking-wider" style={{width:'15%'}}>Tag</th>
                    <th className="text-right px-5 py-2.5 text-[10px] font-semibold text-orange-500 uppercase tracking-wider" style={{width:'15%'}}>Debit</th>
                    <th className="text-right px-5 py-2.5 text-[10px] font-semibold text-[#4A7A5D] uppercase tracking-wider" style={{width:'15%'}}>Credit</th>
                    {!isHolderFiltered && <th className="text-right px-5 py-2.5 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider" style={{width:'15%'}}>Balance</th>}
                  </tr>
                </thead>
                <tbody>
                  {!isHolderFiltered && (
                  <tr className="border-b border-[#E8E6DE] bg-[#F3F2ED]">
                    <td className="px-4 py-2 font-mono text-[#5C5C55] whitespace-nowrap">{obDate}</td>
                    <td className="px-3 py-2 font-semibold text-[#1C1C19]">{isCreditCard ? "Outstanding Balance" : "Opening Balance"}</td>
                    <td></td>
                    {isCreditCard ? (
                      <>
                        <td className="px-5 py-2 text-right font-mono text-orange-600 whitespace-nowrap">₹{fmtINR(firstFY.opening_balance)}</td>
                        <td></td>
                      </>
                    ) : (
                      <>
                        <td></td>
                        <td className="px-5 py-2 text-right font-mono text-[#4A7A5D] whitespace-nowrap">₹{fmtINR(firstFY.opening_balance)}</td>
                      </>
                    )}
                    <td className="px-5 py-2 text-right font-mono font-bold text-[#1C1C19] whitespace-nowrap">₹{fmtINR(firstFY.opening_balance)}</td>
                  </tr>
                  )}
                  {allEntries.map((e, i) => (
                    <tr key={e.id || i} className="border-b border-[#E8E6DE] hover:bg-[#FAFAF8]">
                      <td className="px-4 py-1.5 font-mono text-[#5C5C55] whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="px-3 py-1.5 overflow-hidden">
                        <div title={e.merchant} className="text-[#1C1C19] font-medium truncate cursor-help">
                          {(!e.tag || e.tag === 'untagged') ? (e.merchant || '—') : (e.category || 'Other')}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-left overflow-hidden"><TagBadge tag={e.tag} category={e.category} /></td>
                      <td className="px-5 py-1.5 text-right font-mono text-orange-600 whitespace-nowrap overflow-hidden">{e.debit > 0 ? `₹${fmtINR(e.debit)}` : ''}</td>
                      <td className="px-5 py-1.5 text-right font-mono text-[#4A7A5D] whitespace-nowrap overflow-hidden">{e.credit > 0 ? `₹${fmtINR(e.credit)}` : ''}</td>
                      {!isHolderFiltered && <td className="px-5 py-1.5 text-right font-mono text-[#1C1C19] whitespace-nowrap overflow-hidden">₹{fmtINR(e.balance)}</td>}
                    </tr>
                  ))}
                  {allEntries.length === 0 && (
                    <tr className="border-b border-[#E8E6DE]">
                      <td colSpan={isHolderFiltered ? 5 : 6} className="px-5 py-3 text-center text-[#8A8A80] italic">
                        {searchQuery ? 'No matching entries' : 'No entries in this period'}
                      </td>
                    </tr>
                  )}
                  <tr className="bg-[#F3F2ED]">
                    {!isHolderFiltered ? (
                      <>
                        <td className="px-4 py-2 font-mono text-[#5C5C55] whitespace-nowrap">{lastEntryDate}</td>
                        <td className="px-3 py-2 font-semibold text-[#1C1C19]">Closing Balance</td>
                        <td></td>
                        <td className="px-5 py-2 text-right font-mono text-orange-600 whitespace-nowrap">₹{fmtINR(totalDebits)}</td>
                        <td className="px-5 py-2 text-right font-mono text-[#4A7A5D] whitespace-nowrap">₹{fmtINR(totalCredits)}</td>
                        <td className="px-5 py-2 text-right font-mono font-bold text-[#1C1C19] whitespace-nowrap">₹{fmtINR(lastFY.closing_balance)}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2"></td>
                        <td className="px-3 py-2 font-semibold text-[#1C1C19]">Total</td>
                        <td></td>
                        <td className="px-5 py-2 text-right font-mono text-orange-600 whitespace-nowrap">₹{fmtINR(totalDebits)}</td>
                        <td className="px-5 py-2 text-right font-mono text-[#4A7A5D] whitespace-nowrap">₹{fmtINR(totalCredits)}</td>
                        <td></td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()
      }
    </div>
  );
}

function SummaryCard({ icon, label, value, color, sub }) {
  return (
    <div className="bg-white border border-[#E2E0D8] rounded-lg p-3.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={color}>{icon}</span>
        <span className="text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">{label}</span>
      </div>
      <p className={`text-base font-mono font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-[#5C5C55] mt-0.5">{sub}</p>
    </div>
  );
}

function TabBtn({ active, onClick, label, testId, icon, reconciled, closed }) {
  const base = closed
    ? (active ? 'bg-[#F5F5F5] text-[#9E9E9E] shadow-sm ring-1 ring-[#BDBDBD]/30' : 'text-[#BDBDBD] hover:text-[#9E9E9E] hover:bg-[#F5F5F5]/60')
    : reconciled
      ? (active ? 'bg-[#E8F5E9] text-[#2E7D32] shadow-sm ring-1 ring-[#4CAF50]/30' : 'text-[#4A7A5D] hover:bg-[#E8F5E9]/60')
      : (active ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]');
  return (
    <button data-testid={testId} onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${base}`}>
      {icon}{label}
      {reconciled && <Check size={12} className={closed ? 'text-[#BDBDBD]' : 'text-[#4CAF50]'} />}
    </button>
  );
}
